import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Calendar, Euro, User, Mail, Phone, MapPin,
  Truck, CheckCircle, Clock, AlertCircle, FileText, Download, 
  Edit, Trash2, Send, Bell, Gift
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import LoadingSpinner from '../LoadingSpinner';
import { useToast } from '../../hooks/use-toast';
import OrderInvoices from './OrderInvoices';
import { computeFiscalSummary, formatCurrencyMillesimi, computeAccisaPerBottle, computeContrassegnoPerBottle } from '../../utils/fiscalCalc';
import ProcurementChecklist from './ProcurementChecklist';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    status: '',
    trackingNumber: '',
    carrier: '',
    adminNotes: '',
    project_name: ''
  });

  useEffect(() => {
    if (adminToken && id) {
      loadOrder();
    }
  }, [id, adminToken]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Order data received:', data);
        // L'API ritorna {success: true, order: {...}}
        let orderData = null;
        if (data.success && data.order) {
          orderData = data.order;
          setOrder(orderData);
        } else if (data.id) {
          // Fallback se ritorna direttamente l'ordine
          orderData = data;
          setOrder(orderData);
        } else {
          console.error('Order data format unexpected:', data);
        }

        // Carica dati utente se disponibile userId
        if (orderData && orderData.userId) {
          loadUserData(orderData.userId);
        }
      } else {
        console.error('Errore nel caricamento ordine, status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Errore:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserData = async (userId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUserData(data.user);
          console.log('✅ User data loaded:', data.user.email);
        }
      }
    } catch (error) {
      console.error('Errore caricamento dati utente:', error);
    }
  };

  const handleEdit = () => {
    setEditData({
      status: order.status || '',
      trackingNumber: order.trackingNumber || '',
      carrier: order.carrier || '',
      adminNotes: order.adminNotes || '',
      project_name: order.project_name || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${id}/status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editData)
        }
      );

      if (response.ok) {
        toast({
          title: "Ordine aggiornato!",
          description: "Le modifiche sono state salvate con successo.",
        });
        setShowEditModal(false);
        loadOrder(); // Ricarica i dati
      } else {
        throw new Error('Errore aggiornamento');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile aggiornare l'ordine.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare questo ordine? Questa azione è irreversibile.')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Ordine cancellato",
          description: "L'ordine è stato eliminato con successo.",
        });
        navigate('/admin/orders');
      } else {
        throw new Error('Errore cancellazione');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile cancellare l'ordine.",
        variant: "destructive"
      });
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${id}/download-pdf`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ordine_${order.orderNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast({
          title: "PDF scaricato",
          description: `PDF ordine #${order.orderNumber} scaricato con successo`
        });
      } else {
        throw new Error('Errore download');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile scaricare il PDF.",
        variant: "destructive"
      });
    }
  };

  const handleSendEmail = async () => {
    if (!window.confirm('Vuoi inviare una email di conferma ordine al cliente?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/send-confirmation`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Email inviata!",
          description: "La conferma ordine è stata inviata al cliente.",
        });
        // Ricarica ordine per aggiornare Timeline
        loadOrder();
      } else {
        throw new Error('Errore invio email');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile inviare l'email.",
        variant: "destructive"
      });
    }
  };


  const handleSendPaymentReminder = async () => {
    // Get payment method from correct field
    const paymentMethod = (order.paymentInfo?.method || order.paymentMethod || '').toLowerCase();
    let confirmMessage = '';
    
    if (!paymentMethod) {
      toast({
        title: "Metodo pagamento non trovato",
        description: "Non è possibile inviare il reminder senza un metodo di pagamento specificato.",
        variant: "destructive"
      });
      return;
    }
    
    if (paymentMethod.includes('paypal') || paymentMethod.includes('stripe') || paymentMethod.includes('card') || paymentMethod.includes('carta')) {
      confirmMessage = 'Vuoi inviare un reminder con il link per completare il pagamento online?';
    } else if (paymentMethod.includes('bonifico') || paymentMethod.includes('bank')) {
      confirmMessage = 'Vuoi inviare un reminder con i dettagli per effettuare il bonifico bancario?';
    } else {
      toast({
        title: "Metodo non supportato",
        description: `Il metodo di pagamento "${paymentMethod}" non supporta reminder automatici.`,
        variant: "destructive"
      });
      return;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${id}/send-payment-reminder`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Reminder inviato!",
          description: `Email di reminder pagamento inviata al cliente (${data.paymentMethod}).`,
        });
        // Ricarica ordine per aggiornare Timeline
        loadOrder();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Errore invio reminder');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: error.message || "Impossibile inviare il reminder.",
        variant: "destructive"
      });
    }
  };

  const handleMarkAsGift = async () => {
    const giftReason = window.prompt('Motivo omaggio (es: Campione, Omaggio promozionale):', 'Campione');
    
    if (!giftReason) return;

    if (!window.confirm(`Marcare questo ordine come omaggio? L'importo verrà azzerato e lo stato pagamento settato su "Pagato".`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${id}/mark-as-gift?gift_reason=${encodeURIComponent(giftReason)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Ordine marcato come omaggio!",
          description: `L'ordine è stato marcato come omaggio: ${giftReason}`,
        });
        await loadOrder(); // Reload to show updated data
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Errore');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: error.message || "Impossibile marcare come omaggio.",
        variant: "destructive"
      });
    }
  };


  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_payment: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'In attesa pagamento' },
      deposit_confirmed: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Acconto confermato (50%)' },
      balance_confirmed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Saldo confermato' },
      payment_confirmed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Pagamento confermato' },
      waiting_label: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'In attesa dei file' },
      label_review: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'In revisione etichetta' },
      procurement: { bg: 'bg-cyan-100', text: 'text-cyan-800', label: 'Approvvigionamento' },
      in_production: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'In produzione' },
      production_completed: { bg: 'bg-lime-100', text: 'text-lime-800', label: 'Produzione completata' },
      to_ship: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Da spedire' },
      to_ship_gift: { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Da spedire omaggio' },
      pickup_at_location: { bg: 'bg-violet-100', text: 'text-violet-800', label: 'Ritiro in sede' },
      shipped: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Spedito' },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', label: 'Consegnato' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Annullato' }
    };
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  if (!order) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Ordine non trovato</p>
          <Link to="/admin/orders" className="text-purple-600 hover:text-purple-800 mt-4 inline-block">
            Torna agli ordini
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-gray-900">
                    Ordine #{order.orderNumber || order.id}
                  </h1>
                  {order.project_name && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                      📋 {order.project_name}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-gray-500 text-sm">Creato il {formatDate(order.createdAt)}</p>
                  {/* Link al preventivo originale se esiste */}
                  {order.activityLog && order.activityLog.length > 0 && 
                   order.activityLog[0].details?.quoteId && (
                    <>
                      <span className="text-gray-300">•</span>
                      <Link 
                        to={`/admin/quotes?view=${order.activityLog[0].details.quoteId}`}
                        className="text-purple-600 hover:text-purple-800 text-sm flex items-center gap-1"
                      >
                        <FileText className="w-4 h-4" />
                        Preventivo #{order.activityLog[0].details.quoteNumber}
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
            {getStatusBadge(order.status)}
          </div>

          {/* Azioni */}
          <div className="flex flex-wrap gap-3">
            {/* Mark as Gift - show only if not already a gift */}
            {!order.isGift && (
              <button
                onClick={handleMarkAsGift}
                className="flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                <Gift className="w-4 h-4 mr-2" />
                Marca come Omaggio
              </button>
            )}
            <button
              onClick={handleEdit}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Edit className="w-4 h-4 mr-2" />
              Modifica Stato Ordine
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Scarica PDF
            </button>
            <button
              onClick={handleSendEmail}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Send className="w-4 h-4 mr-2" />
              Invia Email
            </button>
            {/* Payment Reminder Button - Show for unpaid orders */}
            {order.status !== 'completed' && order.status !== 'paid' && order.status !== 'cancelled' && (
              <button
                onClick={handleSendPaymentReminder}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                title={`Status ordine: ${order.status}`}
              >
                <Bell className="w-4 h-4 mr-2" />
                Reminder Pagamento
              </button>
            )}
            <button
              onClick={handleDelete}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Cancella
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonna sinistra */}
          <div className="lg:col-span-2 space-y-6">
            {/* Prodotti */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Package className="w-5 h-5 mr-2" />
                Prodotti
              </h2>
              <div className="space-y-3">
                {order.items?.map((item, index) => (
                  <div key={index} className="border-b pb-3 last:border-b-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium">{item.productName || item.name}</p>
                        {item.description && (
                          <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                        )}
                        {item.format && (
                          <p className="text-xs text-gray-400 mt-0.5">Formato: {item.format}</p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        {item.discount_percent > 0 ? (
                          <>
                            <p className="text-sm text-gray-400 line-through">{formatCurrency(item.unitPrice || item.price || 0)} cad.</p>
                            <p className="font-medium text-green-600">
                              {formatCurrency((item.unitPrice || item.price || 0) * (1 - item.discount_percent / 100))} cad.
                            </p>
                            <p className="text-xs text-green-600">-{item.discount_percent}% sconto</p>
                          </>
                        ) : (
                          <p className="font-medium">{formatCurrency(item.unitPrice || item.price || 0)} cad.</p>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                      <p className="text-sm text-gray-600">Quantità: {item.quantity}</p>
                      <p className="font-bold text-lg">{formatCurrency(item.totalPrice || (item.quantity * (item.unitPrice || item.price || 0)))}</p>
                    </div>
                    {/* Tasse di Stato per riga (SOLO ADMIN) */}
                    {(() => {
                      const qty = parseInt(item.quantity, 10) || 0;
                      const accisaBottle = computeAccisaPerBottle(item.format, item.alcoholContent || 0);
                      const contrBottle = computeContrassegnoPerBottle(item.format);
                      const accisaTot = accisaBottle * qty;
                      const contrTot = contrBottle * qty;
                      const missingAlc = !parseFloat(item.alcoholContent);
                      return (
                        <div className="mt-2 bg-blue-50 border border-blue-100 rounded p-2 text-[11px] text-blue-900 grid grid-cols-2 gap-x-4 gap-y-0.5" data-testid={`row-fiscal-${index}`}>
                          <div className="col-span-2 flex items-center justify-between font-semibold uppercase text-[9px] tracking-wider text-blue-700 mb-0.5">
                            <span>Tasse di Stato (solo admin)</span>
                            {missingAlc && <span className="text-orange-700 normal-case">⚠️ grado alcolico mancante</span>}
                          </div>
                          <div className="flex justify-between"><span>Accisa/bott.</span><span className="font-mono">{formatCurrencyMillesimi(accisaBottle)}</span></div>
                          <div className="flex justify-between"><span>Contrassegno/bott.</span><span className="font-mono">{formatCurrencyMillesimi(contrBottle)}</span></div>
                          <div className="flex justify-between font-semibold"><span>Accisa totale ({qty}×)</span><span className="font-mono">{formatCurrencyMillesimi(accisaTot)}</span></div>
                          <div className="flex justify-between font-semibold"><span>Contrassegni totali ({qty}×)</span><span className="font-mono">{formatCurrencyMillesimi(contrTot)}</span></div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
              
              {/* Totali */}
              <div className="mt-6 pt-4 border-t space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotale</span>
                  <span>{formatCurrency(order.subtotal || order.totalAmount)}</span>
                </div>
                {order.shippingCost > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Spedizione</span>
                    <span>{formatCurrency(order.shippingCost)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t">
                  <span>Totale</span>
                  <span>{formatCurrency(order.totalAmount)}</span>
                </div>
                {/* Accisa + Contrassegni di Stato (sempre visibili anche a €0,000) */}
                {(() => {
                  const items = order.items || [];
                  const fiscal = computeFiscalSummary(items, 'alcoholContent');
                  const missingAlc = items.filter(it => !parseFloat(it.alcoholContent)).length;
                  return (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-1" data-testid="order-detail-fiscal">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-sm font-semibold text-blue-900">Tasse di Stato (informative)</div>
                        <div className="text-[11px] text-blue-700">{fiscal.totalBottles} bottiglie</div>
                      </div>
                      <div className="flex justify-between items-center text-sm" data-testid="order-detail-accisa">
                        <span className="text-blue-800">Accisa Stato <span className="text-[10px] text-blue-600">(€10,3552/L alcol puro, IVA escl.)</span></span>
                        <span className="font-semibold text-blue-900">{formatCurrencyMillesimi(fiscal.accisa)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm" data-testid="order-detail-contrassegno">
                        <span className="text-blue-800">Contrassegno di Stato <span className="text-[10px] text-blue-600">(per bottiglia, scaglione formato)</span></span>
                        <span className="font-semibold text-blue-900">{formatCurrencyMillesimi(fiscal.contrassegno)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm pt-1 border-t border-blue-200 mt-1">
                        <span className="font-semibold text-blue-900">Totale tasse di Stato</span>
                        <span className="font-bold text-blue-900">{formatCurrencyMillesimi(fiscal.accisa + fiscal.contrassegno)}</span>
                      </div>
                      {missingAlc > 0 && (
                        <div className="mt-2 pt-2 border-t border-blue-200 text-[11px] text-orange-700 bg-orange-50 rounded p-1.5">
                          ⚠️ {missingAlc} {missingAlc === 1 ? 'riga senza' : 'righe senza'} <b>grado alcolico</b>: l&apos;accisa risulta sottostimata. Aggiornare il campo nel prodotto.
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* MOL — Margine Operativo Lordo */}
                {(() => {
                  const items = order.items || [];
                  // Compute revenue NET of VAT (subtotal preferred, fallback to totalAmount/1.22)
                  const revenue = order.subtotal != null
                    ? order.subtotal
                    : items.reduce((sum, it) => sum + ((it.unitPrice || 0) * (it.quantity || 0) * (1 - (it.discount_percent || 0) / 100)), 0);
                  const totalCost = items.reduce((sum, it) => sum + ((it.unitCost || 0) * (it.quantity || 0)), 0);
                  const fiscal = computeFiscalSummary(items, 'alcoholContent');
                  const totalBottles = fiscal.totalBottles;
                  const hasAnyCost = items.some(it => (it.unitCost || 0) > 0);
                  const hasFiscal = fiscal.accisa > 0 || fiscal.contrassegno > 0;
                  if ((!hasAnyCost && !hasFiscal) || revenue === 0) return null;
                  const molNet = revenue - totalCost - fiscal.accisa - fiscal.contrassegno;
                  const molNetPct = revenue > 0 ? (molNet / revenue) * 100 : 0;
                  const color = molNet >= 0 ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-700 bg-red-50 border-red-200';
                  return (
                    <div className={`mt-3 p-3 border rounded-lg space-y-2 ${color}`} data-testid="order-detail-mol">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium">MOL netto — Margine Operativo Lordo</p>
                          <p className="text-xs opacity-75">Ricavi {formatCurrency(revenue)} − Costi {formatCurrency(totalCost + fiscal.accisa + fiscal.contrassegno)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">{formatCurrency(molNet)}</p>
                          <p className="text-xs font-semibold">{molNetPct.toFixed(1)}%</p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-current border-opacity-20 space-y-1 text-sm">
                        {hasAnyCost && (
                          <div className="flex justify-between items-center" data-testid="order-detail-industrial-costs">
                            <span className="font-medium">Costi industriali ({totalBottles} {totalBottles === 1 ? 'bottiglia' : 'bottiglie'})</span>
                            <span className="font-semibold">{formatCurrencyMillesimi(totalCost)}</span>
                          </div>
                        )}
                        {fiscal.accisa > 0 && (
                          <div className="flex justify-between items-center" data-testid="order-detail-accisa">
                            <span className="font-medium">Accisa (€10,3552/L alcol puro)</span>
                            <span className="font-semibold">{formatCurrencyMillesimi(fiscal.accisa)}</span>
                          </div>
                        )}
                        {fiscal.contrassegno > 0 && (
                          <div className="flex justify-between items-center" data-testid="order-detail-contrassegno">
                            <span className="font-medium">Contrassegno di stato</span>
                            <span className="font-semibold">{formatCurrencyMillesimi(fiscal.contrassegno)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {order.isGift && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center text-amber-800">
                      <Gift className="w-5 h-5 mr-2" />
                      <div>
                        <p className="font-semibold">🎁 Ordine Omaggio</p>
                        <p className="text-sm">{order.giftReason || 'Omaggio'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Piano di Pagamento */}
              {order.payment_schedule_type === 'split_50_50' && order.scheduled_payments && order.scheduled_payments.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    💰 Piano di Pagamento
                  </h3>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                    {order.scheduled_payments.map((payment, index) => (
                      <div key={payment.id || index} className="flex justify-between items-center py-2 border-b border-amber-200 last:border-b-0">
                        <div>
                          <p className="font-medium text-amber-900">{payment.label}</p>
                          <p className="text-sm text-amber-700">{payment.due_description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-amber-900">{formatCurrency(payment.amount)}</p>
                          <p className="text-sm text-amber-600">{payment.percentage}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Procurement Checklist - Shows only when status is 'procurement' */}
            <ProcurementChecklist 
              orderId={id} 
              adminToken={adminToken} 
              orderStatus={order.status}
            />

            {/* Info Indirizzi */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Indirizzi Cliente
              </h2>

              <div className="space-y-6">
                {/* Sede Legale / Indirizzo Principale */}
                {userData && (() => {
                  const address = userData.address || userData.businessInfo?.address;
                  const city = userData.city || userData.businessInfo?.city;
                  const zipCode = userData.zipCode || userData.businessInfo?.zipCode;
                  const province = userData.province || userData.businessInfo?.province;

                  if (!address && !city) return null;

                  return (
                    <div className="pb-4 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-purple-600 mb-3">
                        🏢 SEDE LEGALE / INDIRIZZO PRINCIPALE
                      </h3>
                      <div className="space-y-2 text-gray-700 ml-4">
                        {userData.businessInfo?.companyName && (
                          <p className="font-medium">{userData.businessInfo.companyName}</p>
                        )}
                        <p className="font-medium">
                          {userData.firstName} {userData.lastName}
                        </p>
                        {address && <p>{address}</p>}
                        {(zipCode || city) && (
                          <p>{zipCode} {city} {province && `(${province})`}</p>
                        )}
                        {userData.phone && (
                          <div className="flex items-center text-sm text-gray-600 mt-2">
                            <Phone className="w-3 h-3 mr-1" />
                            {userData.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Indirizzo di Spedizione del Cliente */}
                {userData?.customerAddresses?.shippingAddress && (() => {
                  const shipping = userData.customerAddresses.shippingAddress;
                  const hasShipping = shipping.address || shipping.city || shipping.postalCode;

                  if (!hasShipping) return null;

                  return (
                    <div className="pb-4 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-blue-600 mb-3">
                        📦 INDIRIZZO SPEDIZIONE SALVATO
                      </h3>
                      <div className="space-y-2 text-gray-700 ml-4">
                        {(shipping.firstName || shipping.lastName) && (
                          <p className="font-medium">
                            {shipping.firstName} {shipping.lastName}
                          </p>
                        )}
                        {shipping.company && <p className="text-sm">{shipping.company}</p>}
                        {shipping.address && <p>{shipping.address}</p>}
                        {(shipping.postalCode || shipping.city) && (
                          <p>{shipping.postalCode} {shipping.city} {shipping.province && `(${shipping.province})`}</p>
                        )}
                        {shipping.country && <p className="text-sm">{shipping.country}</p>}
                        {shipping.phone && (
                          <div className="flex items-center text-sm text-gray-600 mt-2">
                            <Phone className="w-3 h-3 mr-1" />
                            {shipping.phone}
                          </div>
                        )}
                        {shipping.notes && (
                          <p className="text-sm text-gray-500 italic mt-2">Note: {shipping.notes}</p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Indirizzo di Spedizione dell'Ordine */}
                {order.shippingAddress && (
                  <div>
                    <h3 className="text-sm font-semibold text-green-600 mb-3">
                      🚚 INDIRIZZO SPEDIZIONE ORDINE
                    </h3>
                    <div className="space-y-2 text-gray-700 ml-4">
                      <p className="font-medium">
                        {order.shippingAddress.firstName} {order.shippingAddress.lastName}
                      </p>
                      {order.userEmail && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail className="w-3 h-3 mr-1" />
                          {order.userEmail}
                        </div>
                      )}
                      <p>{order.shippingAddress.street} {order.shippingAddress.streetNumber || ''}</p>
                      <p>{order.shippingAddress.zipCode} {order.shippingAddress.city} ({order.shippingAddress.province})</p>
                      {order.shippingAddress.country && <p>{order.shippingAddress.country}</p>}
                      {(order.phone || order.shippingAddress?.phone) && (
                        <div className="flex items-center text-sm text-gray-600 mt-2">
                          <Phone className="w-3 h-3 mr-1" />
                          {order.phone || order.shippingAddress.phone}
                        </div>
                      )}
                      {order.shippingAddress.additionalInfo && (
                        <p className="text-sm text-gray-500 italic mt-2">{order.shippingAddress.additionalInfo}</p>
                      )}
                    </div>

                    {/* Tracking */}
                    {order.trackingNumber && (
                      <div className="pt-4 border-t border-gray-200 mt-4">
                        <h4 className="text-sm font-semibold text-gray-500 mb-2">TRACKING</h4>
                        <div className="ml-4">
                          <p className="text-sm font-medium">{order.trackingNumber}</p>
                          {order.carrier && <p className="text-sm text-gray-500">Corriere: {order.carrier}</p>}
                        </div>
                      </div>
                    )}

                    {/* Note Admin */}
                    {order.adminNotes && (
                      <div className="pt-4 border-t border-gray-200 mt-4">
                        <h4 className="text-sm font-semibold text-gray-500 mb-2">📝 NOTE ADMIN</h4>
                        <div className="ml-4 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{order.adminNotes}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Messaggio se nessun indirizzo disponibile */}
                {!userData && !order.shippingAddress && (
                  <p className="text-gray-500 text-sm">Nessun indirizzo disponibile</p>
                )}
              </div>
            </div>
          </div>

          {/* Colonna destra */}
          <div className="space-y-6">
            {/* Info Cliente */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <User className="w-5 h-5 mr-2" />
                Cliente
              </h2>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Mail className="w-4 h-4 mr-2 mt-1 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{order.userEmail}</p>
                  </div>
                </div>
                {(order.phone || order.shippingAddress?.phone || order.billingAddress?.phone) && (
                  <div className="flex items-start">
                    <Phone className="w-4 h-4 mr-2 mt-1 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Telefono</p>
                      <p className="font-medium">{order.phone || order.shippingAddress?.phone || order.billingAddress?.phone}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info Pagamento */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Euro className="w-5 h-5 mr-2" />
                Pagamento
              </h2>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Metodo</span>
                  <span className="font-medium">{order.paymentInfo?.method || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Status</span>
                  <span className={`font-medium ${
                    order.paymentStatus === 'completed' || order.paymentInfo?.status === 'completed' || order.status === 'payment_confirmed'
                      ? 'text-green-600' 
                      : 'text-yellow-600'
                  }`}>
                    {order.paymentStatus === 'completed' || order.paymentInfo?.status === 'completed' || order.status === 'payment_confirmed'
                      ? 'Completato' 
                      : (order.paymentInfo?.status || 'Pending')}
                  </span>
                </div>
                {order.paymentInfo?.transactionId && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">ID Transazione</span>
                    <span className="font-mono text-xs">{order.paymentInfo.transactionId}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Timeline
              </h2>
              <div className="space-y-3">
                {/* Order Created */}
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3"></div>
                  <div>
                    <p className="text-sm font-medium">Ordine creato</p>
                    <p className="text-xs text-gray-500">{formatDate(order.createdAt)}</p>
                  </div>
                </div>
                
                {/* Activity Log Events */}
                {order.activityLog && order.activityLog.length > 0 && (
                  <>
                    {order.activityLog.map((activity, index) => {
                      const getActivityColor = (type) => {
                        switch(type) {
                          case 'status_changed': return 'bg-blue-600';
                          case 'payment_reminder_sent': return 'bg-yellow-600';
                          case 'payment_confirmed': return 'bg-green-600';
                          default: return 'bg-gray-600';
                        }
                      };
                      
                      const getActivityIcon = (type) => {
                        switch(type) {
                          case 'payment_reminder_sent': return '📧';
                          case 'status_changed': return '🔄';
                          case 'payment_confirmed': return '✅';
                          default: return '•';
                        }
                      };
                      
                      return (
                        <div key={index} className="flex items-start">
                          <div className={`w-2 h-2 ${getActivityColor(activity.type)} rounded-full mt-2 mr-3`}></div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {getActivityIcon(activity.type)} {activity.description}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(activity.timestamp)}
                            </p>
                            {activity.details && activity.details.sentBy && (
                              <p className="text-xs text-gray-400 mt-1">
                                da {activity.details.sentBy}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                
                {/* Last Update (fallback if no activity log) */}
                {(!order.activityLog || order.activityLog.length === 0) && order.updatedAt && order.updatedAt !== order.createdAt && (
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                    <div>
                      <p className="text-sm font-medium">Ultimo aggiornamento</p>
                      <p className="text-xs text-gray-500">{formatDate(order.updatedAt)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Fatture e Ricevute */}
            <OrderInvoices orderId={order.id} adminToken={adminToken} />
          </div>
        </div>
      </div>

      {/* Modal Modifica */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Modifica Stato Ordine</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={editData.status}
                    onChange={(e) => setEditData({...editData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="pending_payment">In attesa pagamento</option>
                    <option value="deposit_confirmed">💰 Acconto confermato (50%)</option>
                    <option value="balance_confirmed">💰 Saldo confermato</option>
                    <option value="waiting_label">📁 In attesa dei file</option>
                    <option value="payment_confirmed">Pagamento confermato</option>
                    <option value="procurement">📦 Approvvigionamento</option>
                    <option value="in_production">🏭 In Produzione</option>
                    <option value="production_completed">✅ Produzione completata</option>
                    <option value="to_ship">📦 Da spedire</option>
                    <option value="to_ship_gift">🎁 Da spedire omaggio</option>
                    <option value="pickup_at_location">🏢 Ritiro in sede</option>
                    <option value="shipped">Spedito</option>
                    <option value="delivered">Consegnato</option>
                    <option value="cancelled">Annullato</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    📋 Nome Progetto <span className="text-gray-400 font-normal">(facoltativo)</span>
                  </label>
                  <input
                    type="text"
                    value={editData.project_name}
                    onChange={(e) => setEditData({...editData, project_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Es: Amaro dei Lupi, Gin Botanico..."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Identifica facilmente questo ordine personalizzato
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numero Tracking
                  </label>
                  <input
                    type="text"
                    value={editData.trackingNumber}
                    onChange={(e) => setEditData({...editData, trackingNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Es: 123456789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Corriere
                  </label>
                  <select
                    value={editData.carrier}
                    onChange={(e) => setEditData({...editData, carrier: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Seleziona corriere</option>
                    <option value="SDA">SDA</option>
                    <option value="Bartolini">Bartolini</option>
                    <option value="GLS">GLS</option>
                    <option value="DHL">DHL</option>
                    <option value="Poste Italiane">Poste Italiane</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Note Admin
                  </label>
                  <textarea
                    value={editData.adminNotes}
                    onChange={(e) => setEditData({...editData, adminNotes: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Note interne (non visibili al cliente)"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Salva Modifiche
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};


export default OrderDetail;
